import { defineStore, acceptHMRUpdate } from 'pinia';
import isEmpty from 'lodash/isEmpty';
import { fetchStatuses } from '@/stores/api/statuses.api';
import { injectColorsStatus } from '@/stores/helpers/statuses.helper';

export type StatusType = 'idea' | 'feedback' | string;

export interface Status {
    id: string;
    type: StatusType;
    displayOrder: number;
    name: string;
    color: string;
    deletePending: boolean;
    mappings?: Record<string, unknown>;
    [key: string]: unknown;
}

interface StatusesState {
    error: unknown;
    items: Status[];
    fetching: boolean;
}

export function getInitialState (): StatusesState {
    return {
        error: null,
        items: [],
        fetching: false,
    };
}

// Module-level deduplication — not store state; reset via __resetLoadAllPromisesForUnitTests
const loadAllPromises: Record<string, Promise<void> | null> = {
    feedback: null,
    idea: null,
};

export const useStatusesStore = defineStore('statuses', {
    state: getInitialState,
    getters: {
        list: (state) => (type?: StatusType): Status[] => {
            if (type) return state.items.filter((s) => s.type === type);

            return state.items;
        },
        statusItemById: (state) => (id: string): Status | undefined =>
            state.items.find((s) => s.id === id),
    },
    actions: {
        setFetching (fetching: boolean) {
            this.fetching = fetching;
        },
        setItems (items: Status[]) {
            this.items = items;
        },
        appendItems (items: Status[]) {
            this.items.push(...items);
        },
        setError (error: unknown) {
            this.error = error;
        },
        clearItems (type: StatusType) {
            this.items = this.items.filter((s) => s.type !== type);
        },
        loadAll ({ reFetch = false, type = 'idea' }: { reFetch?: boolean; type?: StatusType } = {}): Promise<void> {
            const currentList = this.list(type);
            if (!isEmpty(currentList) && !reFetch) {
                return Promise.resolve();
            }
            if (loadAllPromises[type]) {
                return loadAllPromises[type]!;
            }
            this.setFetching(true);
            const promise: Promise<void> = (fetchStatuses(type) as Promise<Status[]>)
                .then((resStatuses) => {
                    this.clearItems(type);
                    const statuses = injectColorsStatus(resStatuses, type) as Status[];
                    this.appendItems(statuses);
                })
                .catch((error) => {
                    this.setError(error);
                    throw error;
                })
                .finally(() => {
                    this.setFetching(false);
                    loadAllPromises[type] = null;
                });
            loadAllPromises[type] = promise;

            return promise;
        },
        updateStatusList ({ list, type }: { list: Status[]; type: StatusType }) {
            this.clearItems(type);
            const statuses = injectColorsStatus(list, type) as Status[];
            this.appendItems(statuses);
        },
    },
});

export function __resetLoadAllPromisesForUnitTests () {
    for (const key of Object.keys(loadAllPromises)) {
        loadAllPromises[key] = null;
    }
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useStatusesStore, module.hot));
}
